import {
  forwardRef,
  cloneElement,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactNode,
  type ReactElement,
} from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "gold" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Render as the child element (e.g. an anchor tag), merging Button styles onto it. */
  asChild?: boolean;
  children: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-dark text-white hover:bg-brand-gold active:bg-brand-gold-dark disabled:bg-brand-dark/40",
  secondary:
    "border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white active:bg-brand-dark/90 disabled:border-brand-dark/30 disabled:text-brand-dark/30",
  gold:
    "bg-brand-gold text-white hover:bg-brand-gold-dark active:bg-brand-gold-dark/90 disabled:bg-brand-gold/40",
  ghost:
    "text-brand-dark hover:bg-brand-dark/5 active:bg-brand-dark/10 disabled:text-brand-dark/30",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-4 py-2 text-xs tracking-wider min-h-[44px]",
  md: "px-6 py-2.5 text-sm tracking-wider",
  lg: "px-8 py-3.5 text-sm tracking-widest",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      disabled,
      asChild = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const classes = cn(
      "inline-flex items-center justify-center gap-2 rounded-lg font-sans font-medium uppercase transition-all duration-300 ease-out select-none",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2",
      "hover:scale-[1.02] active:scale-[0.98]",
      "disabled:cursor-not-allowed disabled:hover:scale-100",
      variantStyles[variant],
      sizeStyles[size],
      className
    );

    const loadingSpinner = loading ? (
      <Spinner size="sm" className="shrink-0" />
    ) : null;

    // asChild: clone styles onto the single child element (e.g. <Link>)
    if (asChild && isValidElement(children)) {
      return cloneElement(children as ReactElement<Record<string, unknown>>, {
        className: cn(
          classes,
          (children as ReactElement<{ className?: string }>).props.className
        ),
        ref,
        ...props,
      });
    }

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={classes}
        {...props}
      >
        {loadingSpinner}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
